import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { CircularProgress, Box, Typography, Alert } from '@mui/material';
import oidcAuth from '../../services/auth/oidc-client';
import { loginSuccess, loginFailure } from '../../redux/authSlice';
import { buildAuthorizedSession } from '../../utils/tokenRoles';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);
  const hasRunRef = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      if (hasRunRef.current) return;
      hasRunRef.current = true;
      try {
        // Handle OIDC callback
        const user = await oidcAuth.handleCallback();
        
        if (user) {
          dispatch(loginSuccess(buildAuthorizedSession(user)));
          
          // Navigate to return URL or home
          const returnUrl = (user.state as any)?.returnUrl || '/';
          navigate(returnUrl, { replace: true });
        }
      } catch (error) {
        console.error('Authentication callback error:', error);
        setError('Authentication failed. Please try again.');
        
        // Dispatch login failure
        dispatch(loginFailure('Authentication callback failed'));
        
        // Redirect to login after delay
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      }
    };

    handleCallback();
  }, [navigate, dispatch]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      bgcolor="background.default"
    >
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : (
        <>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Completing authentication...
          </Typography>
        </>
      )}
    </Box>
  );
};

export default AuthCallback;
